import React, { useState, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Slider,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryTreeSelect from "../Categories/CategoryTreeSelect/CategoryTreeSelect";
import categoriesData from "../Categories/categories.json";
import { flattenCategories, getCategoryName } from "../Categories/helper";
import BrowseHandymanList from "../BrowseHandyman/BrowseHandymanList";
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import { connect } from "react-redux";
import {
  CategoryAttributeViewModel,
  CreateOrderCommand,
} from "../../configuration/api/apiSchema";
import { createOrder, fetchCategoryAttributes, sendOrderProposal } from "./actions";
import { useNavigate } from "react-router-dom";
import { Autocomplete } from "@react-google-maps/api";

// ----- Types & Enums -----
interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

/** Enum for steps */
enum CreateOrderStep {
  SELECT_CATEGORY = "select-category",
  TASK_DESCRIPTION = "task-description",
  BROWSE_HANDYMAN = "browse-handyman",
}

const parsedData = JSON.parse(JSON.stringify(categoriesData)) as {
  categories: Category[];
};
const mockCategoryTree = parsedData.categories;
const categoryDictionary = flattenCategories(mockCategoryTree);

// For image preview (similar to your portfolio component)
interface FileWithPreview {
  src: string;
  file?: File;
}

// ----- Redux Props -----
interface StateFromProps {
  attributes: CategoryAttributeViewModel[] | null;
  isFetchingAttributes: boolean;
  createOrderLoading: boolean;
  createOrderError: string | null;
  createdOrder: any; // Use your OrderViewModel type if available
}

interface DispatchFromProps {
  fetchCategoryAttributes: (categoryKey: string) => void;
  createOrder: (orderData: CreateOrderCommand) => void;
  sendOrderProposal: (orderId: string, handymanId: string) => void;
}

type Props = StateFromProps & DispatchFromProps;

// ----- Component -----
const CreateOrderPage: React.FC<Props> = (props: Props) => {

  const navigate = useNavigate();

  const {
    attributes,
    isFetchingAttributes,
    createOrderLoading,
    createdOrder,
    createOrderError,
  } = props;

  // Wizard state
  const [currentStep, setCurrentStep] = useState<CreateOrderStep>(
    CreateOrderStep.SELECT_CATEGORY
  );
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | undefined>(undefined);
  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [location, setLocation] = useState("");
  // New fields for due date, price and images:
  const [dueDate, setDueDate] = useState("");
  const [price, setPrice] = useState("");
  const [photos, setPhotos] = useState<FileWithPreview[]>([]);

  // Local state to store dynamic attribute input values
  const [attributeValues, setAttributeValues] = useState<{ [key: string]: any }>({});
  // Flag to indicate that order has been created successfully
  const [orderCreated, setOrderCreated] = useState(false);
  // State for form error messages (used for validations)
  const [formError, setFormError] = useState("");

  const [selectedHandymanId, setSelectedHandymanId] = useState<string | null>(null);
  const [sendingProposal, setSendingProposal] = useState(false);

  const [address, setAddress] = useState("");
  const [autocompleteRef, setAutocompleteRef] = useState<google.maps.places.Autocomplete | null>(null);

  const [handymanFilterAddress, setHandymanFilterAddress] = useState<string | null>(null);
  const [handymanRadiusKm, setHandymanRadiusKm] = useState<number>(10);
  const [autocompleteHandymanRef, setAutocompleteHandymanRef] = useState<google.maps.places.Autocomplete | null>(null);


  const steps = [
    CreateOrderStep.SELECT_CATEGORY,
    CreateOrderStep.TASK_DESCRIPTION,
    CreateOrderStep.BROWSE_HANDYMAN,
  ];

  /** Toggle a single category's checked state */
  const toggleCategory = (catKey: string) => {
    setSelectedCategoryKey(catKey);
    setFormError("");
  };

  // Dropzone logic for images
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    multiple: true,
    onDrop: async (acceptedFiles) => {
      const base64Files = await Promise.all(
        acceptedFiles.map((file) =>
          fileToBase64(file).then((base64) => ({
            src: base64,
            file,
          }))
        )
      );
      setPhotos((prev) => [...prev, ...base64Files]);
    },
  });

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleReset = () => {
    setCurrentStep(CreateOrderStep.SELECT_CATEGORY);
    setSelectedCategoryKey(undefined);
    setTaskName("");
    setTaskDescription("");
    setLocation("");
    setDueDate("");
    setPrice("");
    setPhotos([]);
    setAttributeValues({});
    setOrderCreated(false);
    setFormError("");
    setSelectedHandymanId(null);
    setSendingProposal(false);

    navigate("/orders")
  };

  // When the createdOrder prop is set (and orderCreated flag is false), move to next step
  useEffect(() => {
    if (createdOrder && !orderCreated) {
      setOrderCreated(true);
      setCurrentStep(CreateOrderStep.BROWSE_HANDYMAN);
    }
  }, [createdOrder, orderCreated]);

  // Handler for moving to next step
  const handleNext = () => {
    setFormError("");
    if (currentStep === CreateOrderStep.SELECT_CATEGORY) {
      if (!selectedCategoryKey) {
        setFormError("Please select a category.");
        return;
      }
      // Fetch the category attributes from the backend
      props.fetchCategoryAttributes(selectedCategoryKey);
      setCurrentStep(CreateOrderStep.TASK_DESCRIPTION);
    } else if (currentStep === CreateOrderStep.TASK_DESCRIPTION) {
      // Validate static fields
      if (!taskName.trim() || !taskDescription.trim()) {
        setFormError("Task Name and Task Description are required.");
        return;
      }
      // Create payload for order creation
      const orderPayload: CreateOrderCommand = {
        categoryKey: selectedCategoryKey,
        name: taskName,
        description: taskDescription,
        location: location,
        address: address,
        orderAttributeAnswers: attributes
          ? attributes.map((attr) => ({
              categoryAttributeId: attr.id || "",
              value: attributeValues[attr.id || ""] ? String(attributeValues[attr.id || ""]) : "",
            }))
          : [],
        dueDateUtc: dueDate ? new Date(dueDate).toISOString() : null,
        price: price ? Number(price) : null,
        base64Images: photos.length > 0 ? photos.map((photo) => photo.src) : null,
      };
      // Dispatch the create order action
      props.createOrder(orderPayload);
    }
  };

  // Handler for moving back
  const handleBack = () => {
    if (orderCreated) return; // Disallow going back after order creation
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
      setFormError("");
    }
  };

  // Handler when a handyman is selected
  const onSelectHandyman = async (handymanId: string) => {
    setSelectedHandymanId(handymanId);
    setSendingProposal(true);
    try {
      // Send proposal to the selected handyman
      await props.sendOrderProposal(createdOrder.id, handymanId);
      // Show success message and reset
      handleReset();
    } catch (error) {
      console.error("Failed to send proposal:", error);
    } finally {
      setSendingProposal(false);
    }
  };

  // Render dynamic attribute input based on the attribute's input type.
  const renderAttributeInput = (attr: CategoryAttributeViewModel) => {
    const attrId = attr.id || "";
    const label = attr.translatedAtributeContent?.label || "Attribute";
    const defaultValue = attr.translatedAtributeContent?.defaultValue || "";
    const value = attributeValues[attrId] ?? defaultValue;

    switch (attr.inputType) {
      case "Text":
        return (
          <TextField
            fullWidth
            variant="outlined"
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({ ...attributeValues, [attrId]: e.target.value })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ""}
            sx={{ mb: 2 }}
          />
        );
      case "TextArea":
        return (
          <TextField
            fullWidth
            variant="outlined"
            multiline
            minRows={4}
            maxRows={10}
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({ ...attributeValues, [attrId]: e.target.value })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ""}
            sx={{ mb: 2 }}
          />
        );
      case "Checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  setAttributeValues({ ...attributeValues, [attrId]: e.target.checked })
                }
              />
            }
            label={label}
            sx={{ mb: 2 }}
          />
        );
      case "Slider":
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography gutterBottom>{label}</Typography>
            <Slider
              value={typeof value === "number" ? value : 0}
              onChange={(e, newValue) =>
                setAttributeValues({ ...attributeValues, [attrId]: newValue as number })
              }
              valueLabelDisplay="auto"
              min={0}
              max={100}
            />
          </Box>
        );
      case "Dropdown":
        return (
          <TextField
            select
            fullWidth
            variant="outlined"
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({ ...attributeValues, [attrId]: e.target.value })
            }
            sx={{ mb: 2 }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {attr.translatedDropdownOption?.options?.map((option, idx) => (
              <MenuItem key={idx} value={option.value || ""}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case "Number":
        return (
          <TextField
            fullWidth
            variant="outlined"
            label={label}
            type="number"
            value={value}
            onChange={(e) =>
              setAttributeValues({ ...attributeValues, [attrId]: e.target.value })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ""}
            sx={{ mb: 2 }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <Box
        width={{ xs: "95%", sm: "85%", md: "75%" }}
        mx="auto"
        mt={4}
        p={3}
        component={Paper}
        elevation={4}
        sx={{ borderRadius: 2 }}
      >
        {/* Stepper */}
        <Stepper activeStep={steps.indexOf(currentStep)} alternativeLabel>
          {steps.map((step) => (
            <Step key={step}>
              <StepLabel
                onClick={() => {
                  // Allow navigation only if order not yet created and only to steps up to the current one
                  if (!orderCreated && steps.indexOf(step) <= steps.indexOf(currentStep)) {
                    setCurrentStep(step);
                    setFormError("");
                  }
                }}
                sx={{
                  cursor:
                    !orderCreated && steps.indexOf(step) <= steps.indexOf(currentStep)
                      ? "pointer"
                      : "default",
                }}
              >
                {step === CreateOrderStep.BROWSE_HANDYMAN
                  ? "Browse Handyman (optional)"
                  : step.replace("-", " ").toUpperCase()}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Display form error if exists */}
        {formError && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {formError}
          </Alert>
        )}

        {/* Step Content */}
        {currentStep === CreateOrderStep.SELECT_CATEGORY && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Select a Category
            </Typography>
            <CategoryTreeSelect
              selectedCategoryKeys={
                selectedCategoryKey ? new Set([selectedCategoryKey]) : new Set()
              }
              toggleCategory={toggleCategory}
              isSingleSelect={true}
              categories={mockCategoryTree}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Selected:{" "}
              {selectedCategoryKey
                ? getCategoryName(mockCategoryTree, selectedCategoryKey)
                : "None"}
            </Typography>
          </Box>
        )}

        {currentStep === CreateOrderStep.TASK_DESCRIPTION && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Describe Your Task
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Task Name"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="filled"
                  multiline
                  minRows={4}
                  maxRows={10}
                  label="Task Description"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              {/* New fields: Due Date & Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Due Date"
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: "9999-12-31T23:59"
                }}
                sx={{ mb: 2 }}
              />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Price"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
              <Autocomplete
                onLoad={(autocomplete) => setAutocompleteRef(autocomplete)}
                onPlaceChanged={() => {
                  const place = autocompleteRef?.getPlace();
                  if (place?.formatted_address) {
                    setAddress(place.formatted_address);
                  }
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address"
                  placeholder="Enter the address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Autocomplete>
            </Grid>
              {/* Image Upload Area */}
              <Grid item xs={12}>
                <Box
                  {...getRootProps()}
                  p={2}
                  sx={{
                    border: "2px dashed #ccc",
                    textAlign: "center",
                    mb: 2,
                    borderRadius: 1,
                  }}
                >
                  <input {...getInputProps()} />
                  <Typography>
                    Drag and drop images here, or click to select files
                  </Typography>
                </Box>
              </Grid>
              {photos.length > 0 && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {photos.map((photo, index) => (
                      <Grid item key={index} xs={4} sm={3}>
                        <Box position="relative">
                          <img
                            src={photo.src}
                            alt={`Uploaded ${index}`}
                            style={{
                              width: "100%",
                              height: "auto",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          />
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() =>
                              setPhotos((prev) => prev.filter((_, i) => i !== index))
                            }
                            sx={{ position: "absolute", top: 4, right: 4 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
              {/* Render dynamic category attributes */}
              {isFetchingAttributes && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
              {attributes && attributes.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      Additional Details
                    </Typography>
                  </Grid>
                  {attributes.map((attr) => (
                    <Grid item xs={12} key={attr.id}>
                      {renderAttributeInput(attr)}
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
            {createOrderError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {createOrderError}
              </Alert>
            )}
          </Box>
        )}

        {currentStep === CreateOrderStep.BROWSE_HANDYMAN && (
          <Box sx={{ mt: 4 }}>
            {orderCreated && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Order created successfully! Please select a handyman to send them a proposal.
              </Alert>
            )}
            <Typography variant="h6" gutterBottom>
              Browse Handymen
            </Typography>
            {sendingProposal ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
                <Typography ml={2}>Sending proposal to handyman...</Typography>
              </Box>
            ) : (
              <GoogleMapsWrapper>
                <BrowseHandymanList
                preselectedCategoryKey={selectedCategoryKey}
                excludeFilters={["price-range", "name"]}
                address={handymanFilterAddress}
                radiusKm={handymanRadiusKm}
                actionButtonTitle="Select Handyman"
                onAction={onSelectHandyman}
                />
              </GoogleMapsWrapper>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleReset}>
                Close Wizard
              </Button>
            </Box>
          </Box>
        )}

        {/* Stepper Controls */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            disabled={
              currentStep === CreateOrderStep.SELECT_CATEGORY || 
              orderCreated ||
              createOrderLoading
            }
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <Box>
            {currentStep === CreateOrderStep.BROWSE_HANDYMAN && (
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{ mr: 2 }}
              >
                Skip
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={createOrderLoading}
              sx={{ 
                minWidth: 100,
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.7
                }
              }}
            >
              {createOrderLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : currentStep === CreateOrderStep.TASK_DESCRIPTION ? (
                "Create Order"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  attributes: state.order.attributes,
  isFetchingAttributes: state.order.isFetchingAttributes,
  createOrderLoading: state.order.createOrderLoading,
  createOrderError: state.order.createOrderError,
  createdOrder: state.order.createdOrder,
});

export default connect(mapStateToProps, {
  fetchCategoryAttributes,
  createOrder,
  sendOrderProposal,
})(CreateOrderPage);
